import React, { useState, useCallback, useMemo } from "react";
import { useChartTextColor } from "../hooks/useChartTextColor";
import CardLoading from "./CardLoading";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  type LegendProps,
  ResponsiveContainer,
  type TooltipProps,
} from "recharts";
import type { CarrierTrendData } from "../types";
import { toLocalIsoDate } from "../utils/dateHelpers";

interface CarrierTrendChartProps {
  data: CarrierTrendData | undefined;
  isLoading: boolean;
  onExpand?: () => void;
  /** Date-range state — when provided, pickers render in the card body. Start selectable up to 180 days back; range span capped at 180 days. */
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (value: string) => void;
  onEndDateChange?: (value: string) => void;
  /** Validation error message when date range is invalid (span exceeds 90 days or start > end) */
  rangeError?: string;
  /** Reset date range to default 30 days */
  onReset?: () => void;
}

const CarrierTrendChart: React.FC<CarrierTrendChartProps> = ({
  data,
  isLoading,
  onExpand,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  rangeError,
  onReset,
}) => {
  const textColor = useChartTextColor();
  // Track which series are hidden (toggled off via legend click — matches Kendo legend behavior)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const handleLegendClick = useCallback((entry: { dataKey?: string; value?: string }) => {
    const key = entry.dataKey ?? entry.value;
    if (!key) return;
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Date picker bounds — memoized to avoid per-render Date allocations
  const minStartDate = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 180); return toLocalIsoDate(d); }, []);
  const maxEndDate = useMemo(() => toLocalIsoDate(new Date()), []);

  // Build dynamic card title based on whether date pickers are active
  const hasPickers = startDate !== undefined && endDate !== undefined && !!onStartDateChange && !!onEndDateChange;
  const cardTitle = hasPickers
    ? `Flagged by carriers: ${startDate || "?"} to ${endDate || "?"}`
    : "Flagged by carriers for last 30 days";

  // Render card header — title only; date pickers are in the card body
  const showPickers = hasPickers;
  const renderCardHeader = () => (
    <div className="card-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "white" }}>
      <span className="panel-title">{cardTitle}</span>
    </div>
  );

  // Date pickers shared across loading / no-data / chart states
  const renderDatePickers = () =>
    showPickers ? (
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>Start</span>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ maxWidth: 150 }}
          value={startDate ?? ""}
          min={minStartDate}
          max={endDate ?? ""}
          onChange={(e) => onStartDateChange!(e.target.value)}
        />
        <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>End</span>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ maxWidth: 150 }}
          value={endDate ?? ""}
          min={startDate ?? ""}
          max={maxEndDate}
          onChange={(e) => onEndDateChange!(e.target.value)}
        />
        {onReset && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onReset}
            title="Reset to last 30 days"
          >
            Reset
          </button>
        )}
      </div>
    ) : null;

  const renderRangeError = () =>
    rangeError ? (
      <div className="text-danger px-3 pt-2" style={{ fontSize: "0.8rem" }}>
        {rangeError}
      </div>
    ) : null;

  if (isLoading) {
    return (
      <div className="card">
        {renderCardHeader()}
        {renderRangeError()}
        <div className="card-body">
          {renderDatePickers()}
          <CardLoading />
        </div>
      </div>
    );
  }

  if (!data || !data.dates || !data.result) {
    return (
      <div className="card">
        {renderCardHeader()}
        {renderRangeError()}
        <div className="card-body">
          {renderDatePickers()}
          {!rangeError && <p className="text-muted">No data available</p>}
        </div>
      </div>
    );
  }

  // Transform data for Recharts: array of { date, "AT&T": N, "Verizon": N, "T-Mobile": N }
  // Backend already handles Canadian label (returns "Rogers/Bell" instead of "AT&T")
  const chartData = data.dates.map((date, i) => {
    const point: Record<string, string | number> = { date };
    data.result.forEach((series) => {
      point[series.name] = series.data[i] ?? 0;
    });
    return point;
  });

  const seriesNames = data.result.map((s) => s.name);

  // Carrier brand colors matching original implementation
  const CARRIER_COLORS: Record<string, string> = {
    Verizon: "#F59B20",
    "AT&T": "#00a8e0",
    "Rogers/Bell": "#00a8e0",
    "T-Mobile": "#e20074",
  };

  // Calculate y-axis ticks using "nice number" algorithm matching Kendo auto-scaling
  const allValues = chartData.flatMap((point) =>
    seriesNames.map((name) => (point[name] as number) ?? 0)
  );
  const maxValue = Math.max(...allValues, 0);

  const getNiceStep = (max: number): number => {
    if (max <= 0) return 10;
    const roughStep = max / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const residual = roughStep / magnitude;
    if (residual <= 1.5) return magnitude;
    if (residual <= 3) return 2 * magnitude;
    if (residual <= 7) return 5 * magnitude;
    return 10 * magnitude;
  };

  const yStep = getNiceStep(maxValue);
  const yAxisMax = Math.ceil(maxValue / yStep) * yStep || yStep;
  const yTicks = Array.from({ length: yAxisMax / yStep + 1 }, (_, i) => i * yStep);

  // Shared tooltip matching MVC: shows all series values at the hovered date, format N0
  const SharedTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div style={{ background: "#fff", color: "#333", border: "1px solid #ccc", padding: "8px 12px", fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        {payload.map((entry) => (
          <div key={entry.name} style={{ color: entry.color }}>
            {entry.name}: {(entry.value ?? 0).toLocaleString()}
          </div>
        ))}
      </div>
    );
  };

  // Legend with clickable items matching Kendo legend toggle behavior
  // Typed to what our string dataKeys produce; recharts types the payload loosely, hence the cast at <Legend>.
  const renderLegend = (props: { payload?: Array<{ value: string; color: string; dataKey?: string }> }) => {
    const { payload } = props;
    if (!payload) return null;
    return (
      <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 8 }}>
        {payload.map((entry) => {
          const key = entry.dataKey ?? entry.value;
          const isHidden = hiddenSeries.has(key);
          return (
            <span
              key={key}
              onClick={() => handleLegendClick(entry)}
              style={{
                cursor: "pointer",
                color: isHidden ? "#ccc" : entry.color,
                textDecoration: isHidden ? "line-through" : "none",
                fontSize: 13,
                userSelect: "none",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  backgroundColor: isHidden ? "#ccc" : entry.color,
                  borderRadius: 2,
                  marginRight: 4,
                  verticalAlign: "middle",
                }}
              />
              {entry.value}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="card">
      {renderCardHeader()}
      {renderRangeError()}
      <div className="card-body d-flex flex-column" style={{ height: 459, position: "relative" }}>
        {renderDatePickers()}
        <ResponsiveContainer width="100%" style={{ flex: 1 }}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            {/* X-axis: show all dates (interval=0), rotation -90 matching MVC categoryAxis */}
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: textColor }}
              angle={-90}
              textAnchor="end"
              height={90}
              interval={0}
            />
            {/* Value axis: nice-number ticks matching Kendo auto-scaling, format N0 */}
            <YAxis
              ticks={yTicks}
              domain={[0, yAxisMax]}
              tick={{ fontSize: 11, fill: textColor }}
              allowDecimals={false}
            />
            {/* Shared tooltip matching MVC tooltip: shared=true, format=N0 */}
            <Tooltip content={SharedTooltip} />
            {/* Legend at top matching MVC legend position */}
            <Legend verticalAlign="top" content={renderLegend as LegendProps["content"]} />
            {seriesNames.map((name) => (
              <Line
                key={name}
                type="linear"
                dataKey={name}
                stroke={CARRIER_COLORS[name] ?? "#8884d8"}
                strokeWidth={2}
                dot={{ r: 3 }}
                hide={hiddenSeries.has(name)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {onExpand && (
          <button type="button" onClick={onExpand} style={{ position: "absolute", bottom: 8, left: 8, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
            <img src="/images/dashboard/expand-icon.svg" className="expand" alt="expand" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CarrierTrendChart;
